import {useNavigate, useOutletContext, useParams} from 'react-router-dom';
import React, {useEffect, useMemo, useState} from 'react';
import withPaginationV2 from '../../../hoc/pagination/withPaginationV2';
import AdmicityTableV2 from '../../../shared-components/Table/V2/AdmicityTableV2';
import {
    useGetEmployeeNotificationFormsQuery,
    useGetFormsByEmployeeQuery,
    useLazyGetEmployeeNotificationFormResponsesQuery
} from '../../../api/services/formRequestsService';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import ContentPasteOffRoundedIcon from '@mui/icons-material/ContentPasteOffRounded';
import {FORM_REQUEST_STATUSES, FORM_REQUEST_STATUSES_NAMES} from '../../../constants/formRequestStatuses';
import FILTER_OPERATIONS from '../../../constants/filterOperations';
import AdmicityStatus from '../../../shared-components/AdmicityStatus';
import {getFormRequestStatusInfo} from '../RequestAndResponses/common/common';
import {formatDate} from '../../../utility/dateUtil';
import PropTypes from 'prop-types';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
    downloadFormResponsePdfReport,
    useLazyGenerateFormResponsesReportByEmployeeQuery
} from '../../../api/services/filesService';
import useFileDownloader from '../../../utility/hooks/useFileDownloader';
import useNotification from '../../../utility/hooks/useNotification';
import AdmicityTabs from '../../../shared-components/AdmicityTabs';
import Paper from '@mui/material/Paper';
import {useTheme} from '@mui/material/styles';
import NotificationFormsTable from '../../../shared-components/NotificationFormsTable';
import ApprovalFormsTable from '../../../shared-components/ApprovalFormsTable';
import {getApprovalStatusInfo} from '../../../constants/FormApprovalStatuses';

/* eslint-disable react/prop-types */
const columns = [
    {
        accessorKey: 'form.name',
        header: 'Form Name',
        filterFn: FILTER_OPERATIONS.CONTAINS,
    },
    {
        accessorKey: 'status',
        header: 'Status',
        Cell: ({cell}) => <AdmicityStatus {...getFormRequestStatusInfo(cell.getValue())} />,
        filterVariant: 'select',
        filterFn: FILTER_OPERATIONS.EQUALS,
        filterSelectOptions: Object.values(FORM_REQUEST_STATUSES)
            .map(statusId => ({
                value: statusId,
                label: FORM_REQUEST_STATUSES_NAMES[statusId]
            })),
    },
    {
        accessorKey: 'expirationDate',
        header: 'Expiration Date',
        Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
        enableColumnFilter: false
    },
    {
        accessorKey: 'editedBy.fullname',
        header: 'Submitted By',
        enableColumnFilter: false
    },
    {
        accessorKey: 'editedAt',
        header: 'Submitted At',
        Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalStage',
        header: 'Approval Stage',
        Cell: ({ row }) => {
            return row.original?.approvalStage ?? '';
        },
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalStageProgress',
        header: 'Approval Progress',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalStatus',
        header: 'Approval Status',
        Cell: ({cell: {getValue}}) => (
            <AdmicityStatus {...getApprovalStatusInfo(getValue())} />
        ),
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvedBy',
        header: 'Approved By',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvedAt',
        header: 'Approved At',
        Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalNote',
        header: 'Approval Note',
        enableColumnFilter: false,
    },
];

/* eslint-enable react/prop-types */

const generalFormsTableColumns = [
    {
        accessorKey: 'name',
        header: 'Form Name',
        filterFn: FILTER_OPERATIONS.CONTAINS
    }
];

const EmployeeForms = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(`${theme.breakpoints.down('sm')}, (max-height:600px)`);
    const {id: employeeId} = useParams();
    const navigate = useNavigate();
    const downloadFile = useFileDownloader();
    const {showSuccessNotification} = useNotification();
    const [generateFormResponsesReportByEmployee] = useLazyGenerateFormResponsesReportByEmployeeQuery();
    const {key, formsState, setFormsState} = useOutletContext();
    const [currentTabIndex, setCurrentTabIndex] = useState(formsState?.currentTabIndex ?? 0);
    const [selectedNotificationFormIndex, setSelectedNotificationFormIndex] = useState(formsState?.selectedNotificationFormIndex);
    const {
        data: notificationForms = [],
        isLoading: isLoadingNotificationForms,
        isFetching: isFetchingNotificationForms,
    } = useGetEmployeeNotificationFormsQuery(employeeId, {skip: currentTabIndex !== 1});

    const Table = useMemo(() => withPaginationV2(AdmicityTableV2, {
        func: useGetFormsByEmployeeQuery,
        props: {employeeId}
    }), [employeeId]);

    useEffect(() => {
        return () => {
            setFormsState({currentTabIndex, selectedNotificationFormIndex});
        };
    }, [setFormsState, currentTabIndex, selectedNotificationFormIndex]);

    const downloadPdfReport = async (formRequestId = null, formId = null, isBulk = true) => {
        if (formRequestId && !isBulk) {
            await downloadFile(
                downloadFormResponsePdfReport,
                {
                    formId,
                    formRequestId
                });
        } else {
            await generateFormResponsesReportByEmployee({employeeId});
            showSuccessNotification('The PDFs is being created. It will be sent to your email address when the process is complete.');
        }
    };

    const tabs =
        [
            {
                label: 'Forms',
                component: <Table
                    id={key}
                    variant="elevation"
                    columns={columns}
                    tableProps={{
                        enableSorting: false,
                        enableFullScreenToggle: false
                    }}
                    onRowClick={(params) => navigate(`${params.id}`)}
                    toolbarIconActions={
                        [{
                            icon: <FileDownloadRoundedIcon/>,
                            title: 'Download report',
                            type: 'select',
                            hideIfEmpty: true,
                            menuItems: [
                                {
                                    label: 'Download PDF',
                                    onClick: downloadPdfReport
                                },
                            ]
                        }]
                    }
                    noRowsOverlay={{
                        icon: <ContentPasteOffRoundedIcon/>,
                        text: 'No requests and responses yet'
                    }}
                    rowActions={[
                        {
                            label: 'Download PDF',
                            action: params => downloadPdfReport(params.id, params.form.id, false),
                            disabled: row => row.status !== FORM_REQUEST_STATUSES.submitted
                        }
                    ]}
                />
            },
            {
                label: 'Notification Forms',
                component: <NotificationFormsTable
                    data={notificationForms}
                    columns={generalFormsTableColumns}
                    isLoading={isLoadingNotificationForms || isFetchingNotificationForms}
                    selectedRowIndex={selectedNotificationFormIndex}
                    setSelectedRowIndex={setSelectedNotificationFormIndex}
                    navigate={navigate}
                    personId={employeeId}
                    fetchResponsesFn={useLazyGetEmployeeNotificationFormResponsesQuery}
                />
            },
            {
                label: 'Approvals',
                component: <ApprovalFormsTable
                    employeeId={employeeId}
                    navigate={navigate}
                />
            }
        ];

    return (
        <Paper
            variant="outlined"
            sx={
                isSmallScreen
                    ? {}
                    : {
                        minHeight: '100%',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
        >
            <AdmicityTabs
                tabs={tabs}
                tabIndex={currentTabIndex}
                onChange={(index) => {
                    setCurrentTabIndex(index);
                    setSelectedNotificationFormIndex(undefined);
                }}
                displayDivider
                slots={{
                    panel: {
                        paddingRight: 0,
                        paddingLeft: 0
                    }
                }}
            />
        </Paper>
    );
};

EmployeeForms.propTypes = {
    employeeId: PropTypes.number
};

export default EmployeeForms;